// Copyright 2026 TokenLat. Licensed under the Apache License, Version 2.0.
import type { GetTypeListData, GetTypeListRequest } from './idl/developer_api';
import { DeveloperApi } from './developer-api';

// No completed-response cache: each refresh reads current token permissions.
// The page lifetime isolates browser sessions; account events advance its epoch.
let generation = 0;
let loggedOut = false;
const pending = new Map<string, Promise<GetTypeListData>>();
const listeners = new Set<() => void>();

export class StaleModelRequest extends Error {}

const tokenSettingsEvent = 'tl-coze:open-token-settings';
// UI navigation only. Opening settings never changes a token or grants access.
export const openTokenLatSettings = () => {
  window.dispatchEvent(new Event(tokenSettingsEvent));
};
export const onOpenTokenLatSettings = (open: () => void) => {
  window.addEventListener(tokenSettingsEvent, open);
  return () => window.removeEventListener(tokenSettingsEvent, open);
};
export const canConfigureModelToken = (state?: string) =>
  [
    'no_default_token',
    'default_token_unavailable',
    'group_unavailable',
    'no_matching_models',
  ].includes(state ?? '');

export function modelCreationErrorState(error: unknown): string | undefined {
  if (
    !error ||
    typeof error !== 'object' ||
    !('code' in error) ||
    String(error.code) !== '100000014' ||
    !('message' in error) ||
    typeof error.message !== 'string'
  ) {
    return;
  }
  const prefix = 'model_selection_unavailable:';
  const state = error.message.startsWith(prefix)
    ? error.message.slice(prefix.length)
    : '';
  return canConfigureModelToken(state) ? state : undefined;
}
export const modelCatalogGeneration = () => generation;
export const invalidateModelCatalog = () => {
  generation++;
  pending.clear();
  listeners.forEach(listener => listener());
};
export const onModelCatalogRefresh = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
if (typeof window !== 'undefined') {
  window.addEventListener('focus', invalidateModelCatalog);
  window.addEventListener('tl-coze:account-changed', event => {
    const logout = (event as CustomEvent<{ loggedOut?: boolean }>).detail
      ?.loggedOut;
    if (typeof logout === 'boolean') {
      loggedOut = logout;
    }
    invalidateModelCatalog();
  });
  window.addEventListener('storage', event => {
    if (event.key === 'tl-coze:account-changed') {
      if (event.newValue?.startsWith('logout:')) {
        loggedOut = true;
      } else if (event.newValue?.startsWith('login:')) {
        // This only permits a fresh authenticated request, never cached access.
        loggedOut = false;
      }
      invalidateModelCatalog();
    }
  });
}

export function readModelCatalog(
  input: GetTypeListRequest,
): Promise<GetTypeListData> {
  if (loggedOut) {
    return Promise.reject(new Error('model_authorization_required'));
  }
  const params = {
    ...input,
    cur_model_id: undefined,
    cur_model_ids: [
      ...new Set(
        [...(input.cur_model_ids ?? []), input.cur_model_id].filter(
          (id): id is string => Boolean(id),
        ),
      ),
    ].sort(),
  };
  const epoch = generation;
  const key = JSON.stringify([
    epoch,
    params.space_id,
    params.model_scene,
    params.cur_model_ids,
  ]);
  const existing = pending.get(key);
  if (existing) {
    return existing;
  }
  const request = DeveloperApi.GetTypeList({ ...params, model: true })
    .then(response => {
      if (epoch !== generation) {
        throw new StaleModelRequest('model_permissions_changed');
      }
      if (response.code || !Array.isArray(response.data?.model_list)) {
        throw new Error('model_list_load_failed');
      }
      return response.data;
    })
    .finally(() => {
      if (pending.get(key) === request) {
        pending.delete(key);
      }
    });
  pending.set(key, request);
  return request;
}

export function modelSelectionMessage(state?: string) {
  const zh =
    typeof navigator !== 'undefined' && navigator.language.startsWith('zh');
  const messages: Record<string, [string, string]> = {
    loading: ['正在刷新可用模型…', 'Refreshing available models…'],
    no_default_token: [
      '请先在账号设置中选择默认令牌。',
      'Choose a default token in account settings.',
    ],
    default_token_unavailable: [
      '当前默认令牌已失效，请更换令牌。',
      'The default token is unavailable. Choose another token.',
    ],
    group_unavailable: [
      '默认令牌所属分组不可用，请调整令牌。',
      'The default token group is unavailable.',
    ],
    no_matching_models: [
      '没有匹配的可用模型，请检查令牌权限或联系管理员同步模型。',
      'No matching models. Check token permissions or ask an admin to sync models.',
    ],
    load_failed: [
      '模型列表加载失败，已保留原选择，请刷新重试。',
      'Could not load models. Your selection is preserved. Retry refresh.',
    ],
    source_unbound: [
      '原模型尚未关联 TokenLat 来源。',
      'The saved model is not linked to TokenLat.',
    ],
    local_disabled: ['原模型已停用。', 'The saved model is disabled.'],
    source_unavailable: [
      '原模型来源已停用或不存在。',
      'The saved model source is unavailable.',
    ],
    token_model_denied: [
      '当前默认令牌不允许使用原模型。',
      'The default token does not allow the saved model.',
    ],
    model_missing: [
      '原模型已不存在，原配置仍保留。',
      'The saved model is missing. Its configuration is preserved.',
    ],
  };
  return messages[state ?? '']?.[zh ? 0 : 1] ?? '';
}
